import { computed, ComputedRef, Ref, ref, set, del } from 'vue';
import { castArray, difference, omit, cloneDeep } from 'lodash-es';
import api from '@/common/utils/api';
import mergeFn from '@/common/utils/merge';
import { DEFAULT_REQUEST_VIEW } from '@/common/store/modules/common/common.constants';
import cleanObjectProperties from '@/common/utils/cleanObjectProperties';
import useAccumulatedAction, { AccumulatedAction } from '@/common/store/pinia/common/useAccumulatedAction';
import { getNextMarker, isPageLast, applyPagingParams } from '@/common/store/pinia/common/paging';
import { formatArrayIn } from '@/common/utils/formatQueryParams';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import CancellationError from '@/common/utils/errors/CancellationError';
import threadRequests from '@/common/utils/threadRequests';
import type { UnwrapRef } from 'vue';
import { takeLastByKey, abortByKey } from '@/common/utils/takeLastByKey';

export interface FetchOptions {
  includeId?: ID | ID[];
  marker?: ID;
  limit?: number;
  view?: string;
  params?: object;
  requestOptions?: object;
  abortController?: AbortController;
}

export type FetchItemsParams = {
  [key: string]: any;
}

type ViewItem = {
  list: ID[];
  count: null | number;
  loading: boolean;
  marker: ID | undefined;
  isLastPage: boolean;
  requestParams: Record<string, any>;
  error: Error | null;
}

export type ItemsMap<TItem> = Ref<Record<ID, TItem>>
export type GetItemById<TMappedItem> = ComputedRef<(id?: ID) => TMappedItem | undefined>;
export type GetItems<TMappedItem> = ComputedRef<TMappedItem[]>;
export type GetItemsByView<TMappedItem> = ComputedRef<(view: string) => TMappedItem[]>;
export type GetItemByProp<TMappedItem> = ComputedRef<<K extends keyof TMappedItem>(prop: K) => (value: TMappedItem[K]) => TMappedItem | undefined>;
export type GetItemByProps<TMappedItem> = ComputedRef<(props: Partial<TMappedItem>) => TMappedItem | undefined>;
export type GetItemsByProp<TMappedItem> = ComputedRef<<K extends keyof TMappedItem>(prop: K) => (value: TMappedItem[K]) => TMappedItem[]>;
export type FindItemByProps<TMappedItem> = ComputedRef<({ index, ...props }: { index: number; [p: string]: any }) => TMappedItem | undefined>;
export type ViewLists = Ref<Record<string, ViewItem>>;

const createEmptyView = (): ViewItem => ({
  list: [],
  count: null,
  loading: false,
  marker: undefined,
  requestParams: {},
  isLastPage: false,
  error: null
});

export type CommonPiniaStore<TItem, TMappedItem> = {
  itemKey: keyof TItem;
  itemsMap: ItemsMap<TItem>;
  viewLists: ViewLists;
  findItemByProps: FindItemByProps<TMappedItem>;
  getCount: ComputedRef<number|null>;
  getItemById: GetItemById<TMappedItem>;
  getItemByProp: GetItemByProp<TMappedItem>;
  getItemByProps: GetItemByProps<TMappedItem>;
  getItems: GetItems<TMappedItem>;
  getItemsByProp: GetItemsByProp<TMappedItem>;
  getItemsByView: GetItemsByView<TMappedItem>;
  getViewList: ComputedRef<(view?: string) => ViewItem | undefined>;
  addViewList: (view: string) => ViewItem;
  changeItem: (data: Partial<TItem>, addToList?: boolean, view?: string) => void;
  create: (body: Partial<TItem>, event?: object) => Promise<FetchResponse>;
  deleteView: (view: string) => void;
  fetchItem: (id: ID, options?: FetchOptions) => Promise<any>;
  fetchLastItem: (id: ID, options?: FetchOptions) => Promise<void>;
  abortFetchItem: (id: ID) => Promise<void>;
  abortAndRemoveItem: (id: ID) => Promise<void>;
  fetchItems: (payload?: object, options?: FetchOptions) => Promise<any>;
  fetchAllItems: (payload?: object, options?: FetchOptions) => Promise<void>;
  fetchItemsWithFilters: (id: ID | ID[], options?: FetchOptions) => Promise<any>;
  fetchItemsWithFiltersThrottled: AccumulatedAction;
  mergeItem: (data: Partial<TItem>) => void;
  pushData: (data: TItem[], view?: string) => void;
  remove: (id: ID, payload?: object) => Promise<any>;
  removeItem: (id: ID) => void;
  removeItems: (id: ID[]) => void;
  resetMarker: (view: string) => void;
  runCustomAction: (action: string, id: ID, payload?: object, method?: string) => Promise<any>
  setData: (data: TItem[], view?: string) => void;
  update: (id: ID, body: Partial<TItem>, event?: object) => Promise<any>;
  $reset: () => void;
  deleteItems: (ids: ID[], threadsCount?: number) => Promise<void[]>;
};

export type ExtractStoreItem<T> = T extends UnwrapRef<CommonPiniaStore<any, infer U>> ? U : never;

export type Options<TItem, TItemMapped> = {
  getUrl: () => Promise<string>|string;
  plural?: (arg: any) => TItem[],
  itemKey?: keyof TItem,
  mapItem?: (arg: TItem) => TItemMapped,
  updateMethod?: 'put' | 'patch',
  dataChangeMode?: 'direct' | 'task' | 'event'
  pagination?: 'none' | 'common' | 's3'
}

const FETCH_MARKER_LIMIT = 20;

export default function useCommonStore<TItem extends object, TMappedItem extends object>({
  getUrl,
  plural = ({ data }) => data,
  itemKey = 'id' as keyof TItem,
  mapItem = (item: TItem): TMappedItem => item as unknown as TMappedItem, // todo: find a way to calculate this type
  updateMethod = 'patch',
  dataChangeMode = 'direct',
  pagination = 'none'
}: Options<TItem, TMappedItem>) {
  function commonStoreFn(): CommonPiniaStore<TItem, TMappedItem> {
    const getId = (item: Partial<TItem>) => item[itemKey] as ID;
    const itemsMap: ItemsMap<TItem> = ref({});
    const viewLists: ViewLists = ref({ [DEFAULT_REQUEST_VIEW]: createEmptyView() });
    const allItems = computed(() => Object.values(itemsMap.value).map((item) => mapItem(item)));

    const getItemById: GetItemById<TMappedItem> = computed(() => (id?: ID) => (id && id in itemsMap.value) ? mapItem(itemsMap.value[id]) : undefined);
    const getItems: GetItems<TMappedItem> = computed(() => viewLists.value[DEFAULT_REQUEST_VIEW].list
      .map((id: ID) => getItemById.value(id))
      .filter(Boolean) as TMappedItem[]
    );
    const getItemByProp: GetItemByProp<TMappedItem> = computed(() => (prop: keyof TMappedItem) => (value: TMappedItem[keyof TMappedItem]) => Object.values(allItems.value).find((item) => item[prop] === value));
    const getItemsByProp: GetItemsByProp<TMappedItem> = computed(() => (prop: keyof TMappedItem) => (value: TMappedItem[keyof TMappedItem]) => Object.values(allItems.value).filter((item) => item[prop] === value));
    const getItemByProps = computed(() => (props: Partial<TMappedItem>) => allItems.value.find(item => Object.keys(props).every(key => item[key] === props[key])));

    const getViewList = computed<(view?: string) => ViewItem | undefined>(() => (view: string = DEFAULT_REQUEST_VIEW) => Object.hasOwn(viewLists.value, view) ? viewLists.value[view] : undefined);

    const findItemByProps = computed(() => ({ index, ...props }: { index?: number; [p: string]: any }) => {
      if (index !== undefined) {
        return getItems.value[index];
      }
      return Object.values(allItems.value).find(item => Object.keys(props).every(key => item[key] === props[key]));
    });

    const getCount = computed(() => {
      if (pagination === 'none') {
        return getItems.value.length;
      }
      return viewLists.value[DEFAULT_REQUEST_VIEW].count;
    });

    const getItemsByView: GetItemsByView<TMappedItem> = computed(() => (view: string) => viewLists.value[view]?.list
      .map(id => getItemById.value(id))
      .filter(Boolean) as TMappedItem[] || []);

    const addViewList = (view: string) => {
      const viewList = getViewList.value(view);
      if (viewList) {
        return viewList;
      }
      set(viewLists.value, view, createEmptyView());
      return viewLists.value[view];
    };

    const pushData = (data: TItem[], view = DEFAULT_REQUEST_VIEW) => {
      const viewList = addViewList(view);
      data.forEach((item) => {
        const id = getId(item);
        set(itemsMap.value, id, item);
        if (!viewList.list.includes(id)) {
          viewList.list.push(id);
          if (viewList.count !== null) {
            viewList.count += 1;
          }
        }
      });
    };

    const setData = (data: TItem[], view = DEFAULT_REQUEST_VIEW) => {
      const viewList = addViewList(view);

      viewList.list = data.map((item) => getId(item));
      data.forEach((item) => {
        set(itemsMap.value, getId(item), item);
      });
    };

    const changeItem = (item: Partial<TItem>, addToList = true, view = DEFAULT_REQUEST_VIEW) => {
      const id = getId(item);
      const mergedItem = mergeFn(cloneDeep(itemsMap.value[id]) || {}, item);
      if (addToList) {
        pushData([mergedItem as TItem], view);
        return;
      }
      // For WS updates and Filtered lists
      set(itemsMap.value, id, mergedItem);
    };

    const mergeItem = (item: Partial<TItem>) => {
      const id = getId(item);
      if (id && id in itemsMap.value) {
        set(itemsMap.value, id, mergeFn(itemsMap.value[id], item));
      }
    };

    const removeItem = (id: ID) => {
      Object.values(viewLists.value).forEach((viewList) => {
        if (viewList.list.includes(id)) {
          viewList.list = viewList.list.filter(it => it !== id);
          if (viewList.count !== null) {
            viewList.count -= 1;
          }
        }
      });
      del(itemsMap.value, id);
    };

    const removeItems = (ids: ID[]) => {
      const idsSet = new Set(ids);
      Object.values(viewLists.value).forEach((viewList) => {
        const removedCount = viewList.list.filter(it => idsSet.has(it)).length;
        viewList.list = viewList.list.filter(it => !idsSet.has(it));
        if (viewList.count !== null && removedCount > 0) {
          viewList.count -= removedCount;
        }
      });
      ids.forEach((id) => {
        del(itemsMap.value, id);
      });
    };

    const fetchItems = async (payload: FetchItemsParams = {}, options: FetchOptions = {}) => {
      const {
        includeId,
        view = DEFAULT_REQUEST_VIEW,
        marker,
        limit = FETCH_MARKER_LIMIT,
        ...requestOptions
      } = options;

      const includeIds = castArray(includeId);
      const viewList = addViewList(view);
      let requestParams = cleanObjectProperties(payload);

      if (pagination !== 'none') {
        viewList.marker = marker;
        requestParams = applyPagingParams[pagination]?.(requestParams, {
          marker,
          limit
        });
      }

      viewList.loading = true;
      viewList.error = null;

      try {
        const url = await getUrl();
        const response = await api.get<TItem[]>(url, requestParams, requestOptions);
        const rawItems = plural(response.data);

        if (pagination !== 'none') {
          if (viewList.marker) {
            pushData(rawItems, view);
          } else {
            setData(rawItems, view);
          }
          viewList.requestParams = cleanObjectProperties(payload);

          const nextMarker = getNextMarker[pagination](response);
          const isLastPage = isPageLast[pagination](response, nextMarker, rawItems, limit);

          viewList.isLastPage = isLastPage;

          viewList.marker = isLastPage ? undefined : nextMarker;
        } else {
          setData(rawItems, view);
        }

        viewList.count = response.data.count;

        const newIds = includeIds.filter(id => !viewList.list.includes(id) && getItemById.value(id));
        if (newIds.length) {
          viewList.list = viewList.list.concat(newIds);
          viewList.count += newIds.length;
        }

        return response;
      } catch (error) {
        if (!(error instanceof CancellationError)) {
          set(viewLists.value, view, createEmptyView());
          viewLists.value[view].error = error as Error;
        }
        throw error;
      } finally {
        viewList.loading = false;
      }
    };

    const fetchAllItems = async (payload: FetchItemsParams = {}, options: FetchOptions = {}) => {
      if (pagination === 'none') {
        await fetchItems(payload, options);
        return;
      }

      const { view = DEFAULT_REQUEST_VIEW, limit = FETCH_MARKER_LIMIT } = options;
      const viewList = addViewList(view);

      viewList.marker = undefined;

      do {
        // eslint-disable-next-line no-await-in-loop
        await fetchItems(payload, {
          ...options,
          view,
          limit,
          marker: viewList.marker
        });
      } while (!viewList.isLastPage);
    };

    const fetchItem = async (id: ID, options: FetchOptions = {}) => {
      const {
        view = DEFAULT_REQUEST_VIEW,
        params,
        ...requestOptions
      } = options;

      try {
        const response = await api.get<TItem>(`${await getUrl()}/${id}`, params, requestOptions);
        pushData([response.data], view);

        return response;
      } catch (error) {
        if (error instanceof NotFoundError) {
          removeItems([id]);
        }
        throw error;
      }
    };

    const fetchLastItem = async (id: ID, options: FetchOptions = {}) => {
      const url = await getUrl();
      takeLastByKey(abortController => fetchItem(id, { ...options, abortController }), `${url}/${id}`);
    };

    const abortFetchItem = async (id: ID) => {
      const url = await getUrl();
      abortByKey(`${url}/${id}`);
    };

    const abortAndRemoveItem = async (id: ID) => {
      await abortFetchItem(id);
      removeItem(id);
    };

    const fetchItemsWithFilters = async (id: ID | ID[], options: FetchOptions = {}) => {
      const {
        view = DEFAULT_REQUEST_VIEW,
        ...requestOptions
      } = options;

      const viewList = addViewList(view);
      const params = omit(viewList.requestParams, ['sort']);
      const ids = castArray(id);

      try {
        const url = await getUrl();
        const response = await api.get<TItem[]>(url, {
          id: formatArrayIn(ids),
          ...params,
        }, requestOptions);

        const rawItems = plural(response.data);
        const itemIds = rawItems.map(getId);
        const itemsToRemove = difference(ids, itemIds);

        if (itemsToRemove.length) {
          viewList.list = viewList.list.filter(it => !itemsToRemove.includes(it));
        }

        if (rawItems.length) {
          pushData(rawItems, view);
        }

        return response;
      } catch (error) {
        viewLists.value[view].error = error as Error;
        throw error;
      }
    };

    const fetchItemsWithFiltersThrottled = useAccumulatedAction<FetchResponse<TItem[]>>(fetchItemsWithFilters);

    const create = async (body: Partial<TItem>, event?: object) => {
      const result = await api.post<TItem>(await getUrl(), {
        ...body,
        event
      });
      if (dataChangeMode === 'direct') {
        changeItem(result.data);
      }
      return result;
    };

    const update = async (id: ID, body: Partial<TItem>, event?: object) => {
      const result = await api[updateMethod]<TItem>(`${await getUrl()}/${id}`, {
        ...body,
        event
      });
      if (dataChangeMode === 'direct') {
        return mergeItem(result.data);
      }
      return result;
    };

    const remove = async (id: ID, payload?: object) => {
      const result = await api.delete<TItem>(`${await getUrl()}/${id}`, payload);
      if (dataChangeMode === 'direct') {
        removeItem(id);
      }
      return result;
    };

    const deleteView = (view: string) => {
      del(viewLists.value, view);
    };

    const runCustomAction = async (action: string,
                                   id: ID,
                                   payload?: object,
                                   method: string = 'post'
    ) => api[method](`${await getUrl()}/${id}/${action}`, payload);

    const resetMarker = (view: string) => {
      if (viewLists.value[view]) {
        set(viewLists.value[view], 'marker', undefined);
      }
    };

    const $reset = () => {
      itemsMap.value = {};
      viewLists.value = { [DEFAULT_REQUEST_VIEW]: createEmptyView() };
    };

    const deleteItems = (ids: ID[], threadsCount = 5) => {
      const tasks = ids.map((id) => () => remove(id));
      return threadRequests(tasks, threadsCount);
    };

    return {
      // state
      itemKey,
      itemsMap,
      viewLists,
      // getters
      findItemByProps,
      getCount,
      getItemById,
      getItemByProp,
      getItemByProps,
      getItems,
      getItemsByProp,
      getItemsByView,
      getViewList,
      // actions
      addViewList,
      changeItem,
      create,
      deleteView,
      abortFetchItem,
      abortAndRemoveItem,
      fetchItem,
      fetchLastItem,
      fetchItems,
      fetchAllItems,
      fetchItemsWithFilters,
      fetchItemsWithFiltersThrottled,
      mergeItem,
      pushData,
      remove,
      removeItem,
      removeItems,
      resetMarker,
      runCustomAction,
      setData,
      update,
      $reset,
      deleteItems
    };
  }

  return commonStoreFn;
}
